import { useTranslations } from 'next-intl';

import { Button } from '@commons/components/Button';
import Card from '@commons/components/Card';
import DrupalImage from '@commons/components/Image/Drupal';
import TopoPatternImage from '@commons/components/Image/TopoPattern';
import Text from '@commons/components/Text';
import Flex from '@commons/containers/Flex';
import { CARD_DISPLAY_IMAGE_SIZES } from '@commons/pagebuilder/nodes/images';

import type { WebinarNodeProps } from '.';
import { WebinarNodeLink } from './GatedDrawer';

/**
 * Webinar node component for the Card display type.
 */

const WebinarNodeCard = (props: WebinarNodeProps) => {
  const t = useTranslations();

  const {
    image,
    title,
    description,
    authors,
    customDate,
    format,
    customLabel,
    ctaLabel,
    alias,
    link,
    isGated,
  } = props;

  const formatLabel = customLabel ? customLabel : format ? format.name : t('nodes.webinar.label');
  const renderedUpperTitle = (
    <Flex gap="xxs" align="center">
      <Text as="span" level="label-lg" weight="medium" transform="uppercase" marginBottom="none">
        {formatLabel}
      </Text>
      <Text as="span" level="body-sm" marginBottom="none">
        /
      </Text>
      <Text as="span" level="body-sm" transform="uppercase" marginBottom="none">
        {customDate}
      </Text>
    </Flex>
  );

  const renderedImage = image ? (
    <DrupalImage {...image} sizes={CARD_DISPLAY_IMAGE_SIZES} />
  ) : (
    <TopoPatternImage alt={title} sizes={CARD_DISPLAY_IMAGE_SIZES} />
  );

  const renderedContent = (
    <Card gap="md" justify="space-between" disabled={!link}>
      <Flex gap="md" direction="column">
        <Card.Overlay>{renderedImage}</Card.Overlay>
        <Flex gap="xs" direction="column">
          {renderedUpperTitle}
          <Card.Title>{title}</Card.Title>
          {authors && (
            <Text level="body-sm" marginBottom="none">
              {t('commons.by', { author: authors })}
            </Text>
          )}
          {description && <Text marginBottom="none">{description}</Text>}
        </Flex>
      </Flex>
      <Button variant="primary" disabled={!link}>
        {ctaLabel || t('commons.learnMore')}
      </Button>
    </Card>
  );

  if (link) {
    return (
      <WebinarNodeLink
        alias={alias}
        link={link}
        isGated={isGated}
        ctaLabel={ctaLabel || t('commons.learnMore')}
        className="has-nested-button"
      >
        {renderedContent}
      </WebinarNodeLink>
    );
  }

  return renderedContent;
};

export default WebinarNodeCard;
